if (!customElements.get('marame-gift-card')) {
  class MarameGiftCard extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.form = this.querySelector('form');
      this.select = this.form?.querySelector('[name="id"]');
      this.button = this.querySelector('[type="submit"]');
      if (!this.form || !this.select || !this.button) return;
      this.events = new AbortController();
      this.media = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.radios = [...this.querySelectorAll('[data-gift-denomination]')];
      this.fields = [...this.querySelectorAll('[data-gift-recipient-field]')];
      this.recipient = this.querySelector('[data-gift-recipient]');
      this.toggle = this.querySelector('[data-gift-recipient-toggle]');
      this.offset = this.querySelector('[data-gift-offset]');
      this.amount = this.querySelector('[data-gift-amount]');
      this.content = this.querySelector('[data-gift-content]');
      this.purchase = this.querySelector('[data-gift-purchase]');
      this.toggle.disabled = false;
      this.querySelector('[data-gift-recipient-fallback]').disabled = true;
      this.querySelector('[data-gift-grid]').hidden = false;
      this.dataset.enhanced = 'true';
      this.offset.value = String(new Date().getTimezoneOffset());
      const date = this.querySelector('[data-gift-date]');
      const today = new Date(), latest = new Date(today);
      latest.setDate(latest.getDate() + 90);
      date.min = this.dateString(today); date.max = this.dateString(latest);
      this.updateRecipient();
      this.updateValue(false);
      this.addEventListener('change', event => {
        const denomination = event.target.closest('[data-gift-denomination]');
        if (denomination) this.selectValue(denomination.value);
        if (event.target === this.toggle) this.updateRecipient();
        if (event.target === this.select) this.updateValue(true);
      }, { signal: this.events.signal });
      this.addEventListener('input', event => {
        if (this.fields.includes(event.target)) { event.target.setCustomValidity(''); event.target.removeAttribute('aria-invalid'); }
        this.updateCount();
      }, { signal: this.events.signal });
      this.form.addEventListener('submit', event => this.beforeSubmit(event), { capture: true, signal: this.events.signal });
      this.form.addEventListener('invalid', event => {
        if (this.fields.includes(event.target)) this.querySelector('[data-gift-note]').open = true;
      }, { capture: true, signal: this.events.signal });
      this.addEventListener('focusin', () => this.syncKeyboard(), { signal: this.events.signal });
      this.addEventListener('focusout', () => { queueMicrotask(() => { if (this.isConnected) this.syncKeyboard(); }); }, { signal: this.events.signal });
      this.busyObserver = new MutationObserver(() => this.syncBusy());
      this.busyObserver.observe(this.button, { attributes: true, attributeFilter: ['aria-disabled', 'class'] });
      this.syncBusy();
      this.media.addEventListener?.('change', () => { if (this.media.matches) this.clearAmountMotion(); }, { signal: this.events.signal });
      document.addEventListener('visibilitychange', () => this.syncArt(), { signal: this.events.signal });
      if (typeof IntersectionObserver !== 'undefined') {
        this.artObserver = new IntersectionObserver(entries => { this.artVisible = entries[0]?.isIntersecting; this.syncArt(); });
        this.artObserver.observe(this.querySelector('[data-gift-art]'));
      }
      if (typeof subscribe === 'function') {
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, event => this.cartError(event));
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.source !== 'product-form' || String(event.productVariantId) !== this.select.value) return;
          this.fields.forEach(field => { field.value = ''; field.setCustomValidity(''); field.removeAttribute('aria-invalid'); });
          this.toggle.checked = false;
          this.updateRecipient();
        });
      }
      this.syncArt();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.busyObserver?.disconnect();
      this.artObserver?.disconnect();
      this.unsubscribeError?.(); this.unsubscribeCart?.();
      this.unsubscribeError = null; this.unsubscribeCart = null;
      this.clearAmountMotion();
    }

    dateString(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    chosen() { return [...this.select.options].find(option => option.value && option.value === this.select.value && !option.disabled); }
    busy() { return this.button.getAttribute('aria-disabled') === 'true' || this.button.classList.contains('loading'); }

    selectValue(id) {
      if (this.busy()) return;
      const option = [...this.select.options].find(candidate => candidate.value === id && !candidate.disabled);
      if (!option) return;
      this.select.value = option.value;
      this.updateValue(true);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    updateValue(animate) {
      const chosen = this.chosen();
      this.radios.forEach(radio => { radio.checked = Boolean(chosen && radio.value === chosen.value); });
      this.button.disabled = !chosen;
      const hasAvailable = [...this.select.options].some(option => option.value && !option.disabled);
      this.button.querySelector('span').textContent = chosen ? this.dataset.addLabel : hasAvailable ? this.dataset.selectLabel : this.dataset.soldOutLabel;
      this.form.querySelector('[name="quantity"]').value = chosen?.dataset.quantity || '1';
      const old = this.amount.textContent, next = chosen?.dataset.price || '';
      this.clearAmountMotion();
      this.amount.textContent = next;
      if (!animate || old === next || !next || this.media.matches || typeof this.amount.animate !== 'function') return;
      if (old) {
        this.amountGhost = this.amount.cloneNode(false);
        this.amountGhost.removeAttribute('data-gift-amount');
        this.amountGhost.setAttribute('aria-hidden', 'true');
        this.amountGhost.textContent = old;
        this.amount.parentElement.append(this.amountGhost);
        this.exitAnimation = this.amountGhost.animate([{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(.9)' }], { duration: 350, easing: 'cubic-bezier(.32,.72,0,1)', fill: 'forwards' });
      }
      this.amountAnimation = this.amount.animate([{ opacity: 0, transform: 'translateY(-10px) scale(.85)' }, { opacity: 1, transform: 'translateY(0) scale(1)' }], { duration: 350, delay: old ? 350 : 0, easing: 'cubic-bezier(.32,.72,0,1)', fill: 'both' });
      const run = this.amountRun;
      this.amountAnimation.onfinish = () => { if (run === this.amountRun) this.clearAmountMotion(); };
      this.amountTimer = setTimeout(() => { if (run === this.amountRun) this.clearAmountMotion(); }, old ? 820 : 470);
    }

    clearAmountMotion() {
      this.amountRun = (this.amountRun || 0) + 1;
      clearTimeout(this.amountTimer);
      [this.amountAnimation, this.exitAnimation].forEach(animation => { if (animation) { animation.onfinish = null; animation.cancel(); } });
      this.amountAnimation = null; this.exitAnimation = null;
      this.amountGhost?.remove(); this.amountGhost = null;
    }

    updateRecipient() {
      const enabled = this.toggle.checked;
      this.recipient.dataset.recipientEnabled = String(enabled);
      this.fields.forEach(field => { field.disabled = !enabled; if (!enabled) field.setCustomValidity(''); });
      this.querySelector('[data-gift-email]').required = enabled;
      this.offset.disabled = !enabled;
      this.updateCount();
    }

    updateCount() {
      this.querySelector('[data-gift-count]').textContent = this.dataset.countLabel.replace('[count]', this.querySelector('[data-gift-message]').value.length);
    }

    beforeSubmit(event) {
      if (this.busy() || !this.chosen()) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (!this.busy()) this.radios.find(radio => !radio.disabled)?.focus();
        return;
      }
      if (this.toggle.checked) {
        const date = this.querySelector('[data-gift-date]');
        if (date.value && (date.value < date.min || date.value > date.max)) date.setCustomValidity(this.dataset.dateError);
        if (!this.form.checkValidity()) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.querySelector('[data-gift-note]').open = true;
          this.form.reportValidity();
        }
      }
    }

    syncBusy() { this.content.inert = this.busy(); }

    syncKeyboard() {
      const active = document.activeElement;
      this.purchase.dataset.keyboard = String(Boolean(this.contains(active) && active?.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select')));
    }

    syncArt() { this.dataset.artPaused = String(document.hidden || this.artVisible === false); }

    cartError(event) {
      if (event.source !== 'product-form' || String(event.productVariantId) !== this.select.value || !this.toggle.checked || typeof event.errors !== 'object') return;
      const names = { email: 'Recipient email', name: 'Recipient name', message: 'Message', send_on: 'Send on' };
      let first;
      Object.entries(event.errors || {}).forEach(([key, messages]) => {
        const field = this.fields.find(candidate => candidate.name === `properties[${names[key]}]`);
        if (!field) return;
        field.setAttribute('aria-invalid', 'true');
        field.setCustomValidity(Array.isArray(messages) ? messages.join(', ') : String(messages));
        first ||= field;
      });
      if (first) {
        this.querySelector('[data-gift-note]').open = true;
        this.content.inert = false;
        first.focus();
        first.reportValidity();
      }
    }
  }
  customElements.define('marame-gift-card', MarameGiftCard);
}
